import { Component, Injector } from "@angular/core";
import { AcaoGovernoService } from "administrativo-lib";
import { AcaoGoverno, BaseResourceListComponent, Coluna, Exercicio, ExercicioService, Filtro, FormatoExportacao, LoginContabil, Orgao, OrgaoService } from "eddydata-lib";
import { ConfirmationService } from "primeng/api";
import { Observable } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as toastr from "toastr";

@Component({
  selector: "app-acao-governo-list",
  templateUrl: "./acao-governo-list.component.html",
})
export class AcaoGovernoListComponent extends BaseResourceListComponent<AcaoGoverno, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  public listaOrgaos: Orgao[] = [];
  public orgaoSelect: number = null;

  public exercicioAprovado: Exercicio;
  public desabilidarProgramacaoFinanceira: string = null;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    private acaoService: AcaoGovernoService,
    private orgaoService: OrgaoService,
    protected exercicioService: ExercicioService,
    protected confirmationService: ConfirmationService,
  ) {
    super(acaoService, injector);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected relations(): string {
    return "acao,executora,meta,meta.programa,meta.unidade,meta.unidade.orgao,meta.programa";
  }

  protected condicoesGrid(): {} {
    return {
      ["ppa.id"]: this.login.ppa.id,
      ["meta.unidade.orgao.id"]: this.orgaoSelect,
    };
  }

  protected ordenacaoGrid(): string[] {
    return ["acao.codigo$ASC"];
  }

  protected filtrosGrid(): Filtro {
    return {
      number: ["custo_total"],
      text: ["acao.codigo", "executora.codigo", "acao.nome", "executora.nome"],
    };
  }

  protected afterInit(): void {
    this.exercicioService
      .obterId(this.login.exercicio.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.exercicioAprovado = res;
        this.orcamentoAprovadoPermissao();
      });
  }

  public async orcamentoAprovadoPermissao() {
    if (this.exercicioAprovado?.orcamento_aprovado) {
      this.desabilidarProgramacaoFinanceira = "true";
    }
  }

  public beforeInit(): void {
    this.usarExtendido = true;
    this.usarFiltroPersonalizado = true;

    this.buscarOrgaos();
    this.orgaoSelect = this.login.orgao.id;
  }

  protected acaoRemover(model: AcaoGoverno): Observable<AcaoGoverno> {
    return null;
  }

  protected colunasRelatorio(): string[] | Coluna[] {
    return [
      { titulo: "Código", coluna: "acao.codigo" },
      { titulo: "Descrição", coluna: "acao.nome" },
      { titulo: "Programa", coluna: "meta.programa.nome" },
      { titulo: "Unidade", coluna: "executora.nome" },
      {
        titulo: `Exercício ${String(this.login.ppa.ppaperiodo.ano1)}`,
        coluna: "custo1",
      },
      {
        titulo: `Exercício ${String(this.login.ppa.ppaperiodo.ano2)}`,
        coluna: "custo2",
      },
      {
        titulo: `Exercício ${String(this.login.ppa.ppaperiodo.ano3)}`,
        coluna: "custo3",
      },
      {
        titulo: `Exercício ${String(this.login.ppa.ppaperiodo.ano4)}`,
        coluna: "custo4",
      },
    ];
  }

  public exportarListagem(formato: FormatoExportacao) {
    const parametros = this.obterParametros();
    parametros["relations"] = "acao,executora,meta,meta.programa";
    this.acaoService
      .filtrar(1, -1, parametros)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((lista) => {
        if (formato === "pdf") {
          this.imprimir(
            `LISTAGEM DE AÇÕES DE GOVERNO: ${this.login.ppa.ppaperiodo.ano1} - ${this.login.ppa.ppaperiodo.ano4}`,
            this.login.usuario.nome,
            this.login.usuario.sobrenome,
            this.login.orgao.nome,
            this.login.brasao,
            "landscape",
            "Listagem Ações de Governo",
            ["auto", "auto", "auto", "auto", "auto", "auto", "auto", "auto"],
            lista.content
          );
        } else {
          this.exportar(formato, lista.content);
        }
      }, this.logErro);
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  public buscarOrgaos(): void {
    const parametros = {
      orderBy: "codigo$ASC",
    };
    parametros["cidade.id"] = this.login.cidade.id;
    this.orgaoService
      .filtrar(1, -1, parametros)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((dados) => {
        this.listaOrgaos = dados.content;
      }, this.logErro);
  }

  public logErro(er: any): void {
    console.error(er);
    toastr.error(er.error.payload, "Erro ao buscar orgão");
    throw er;
  }

  public voltar() {
    switch (this.login.sistema) {
      case "contabil":
      case "controle-interno":
        this.router.navigate(["/planejamento"]);
        break;
      default:
        this.sair();
        break;
    }
  }

  public obterColunasFiltroPersonalizado(): Coluna[] {
    const retorno: Coluna[] = [];

    retorno.push({
      titulo: "Ações de governo",
      coluna: "acao.nome",
      tipo: "String",
      alignment: "center",
      padrao: true,
    });
    retorno.push({
      titulo: "Código",
      coluna: "acao.codigo",
      alignment: "center",
      tipo: "Number",
      padrao: true,
    });
    retorno.push({
      titulo: "Descrição da unidade",
      coluna: "executora.nome",
      tipo: "String",
      padrao: false,
      cols: 3,
    });
    retorno.push({
      titulo: "Código da unidade",
      coluna: "executora.codigo",
      alignment: "center",
      tipo: "String",
      cols: 2,
      padrao: false,
    });
    retorno.push({
      titulo: "Custo",
      coluna: "custo_total",
      alignment: "center",
      tipo: "Number",
      cols: 3,
      padrao: false,
    });
    retorno.push({
      titulo: "Código do programa",
      coluna: "meta.programa.codigo",
      tipo: "String",
      cols: 2,
      padrao: false,
    });
    retorno.push({
      titulo: "Descrição do programa",
      coluna: "meta.programa.nome",
      tipo: "String",
      padrao: false,
    });

    return retorno;
  }

  public removerAcao(acaoGov: AcaoGoverno): void {
    console.log(acaoGov);
    this.confirmationService.confirm({
      message: `Gostaria de seguir com a exclusão da ação de governo ${acaoGov?.acao.codigo}?`,
      header: "Exclusão de ação de governo",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      key: "acao_governo",
      accept: () => {
        this.acaoService
          .remover(acaoGov.id)
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(
            () => {
              toastr.success("Registro removido com sucesso!");
              this.preencherGrid();
            },
            (err) => {
              toastr.error("Falha ao remover registro!");
              console.error(err);
            }
          );
      },
    });
  }

  public podeAlterarAcao(): Boolean {
    return (
      !this.exercicioAprovado?.orcamento_aprovado
    );
  }

  // public reordenarAcoes() {
  //   this.acaoService.reordenarAcoes(this.orgaoSelect, this.login.ppa.id)
  //     .pipe(takeUntil(this.unsubscribe))
  //     .subscribe(res => {
  //       toastr.success('Ações reordenadas com sucesso!');
  //       this.preencherGrid();
  //     }, (err) => {
  //       toastr.error('Falha ao reordenar as ações!');
  //       console.error(err);
  //     });
  // }
}
